import { z } from 'zod';

import type { LoanDataUploadErrorBody, LoanDataUploadOutcome, LoanDataUploadResponse } from './types';

const loanDataUploadResponseSchema: z.ZodType<LoanDataUploadResponse> = z.object({
  upload_id: z.string(),
  s3_key: z.string(),
  total: z.number(),
  success_count: z.number(),
  failure_count: z.number(),
  results: z.array(
    z.discriminatedUnion('status', [
      z.object({
        status: z.literal('success'),
        partner_account_number: z.string().nullish(),
        loan_data_id: z.string().nullish(),
        loan_id: z.string().nullish(),
      }),
      z.object({
        status: z.literal('error'),
        partner_account_number: z.string().nullish(),
        row: z.number().nullish(),
        error: z.string(),
      }),
    ]),
  ),
});

const loanDataUploadErrorBodySchema: z.ZodType<LoanDataUploadErrorBody> = z.object({
  error: z.string(),
  reason: z.string().optional(),
});

export type UploadLoanDataCsvInput = {
  endpoint: string;
  programId: string;
  file: File;
  token: string | null;
  signal?: AbortSignal;
};

export const uploadLoanDataCsv = async ({
  endpoint,
  programId,
  file,
  token,
  signal,
}: UploadLoanDataCsvInput): Promise<LoanDataUploadOutcome> => {
  const formData = new FormData();
  formData.append('program_id', programId);
  formData.append('file', file);

  const headers: Record<string, string> = {};

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  let response: Response;

  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: formData,
      signal,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'network error';

    return { kind: 'network_error', message };
  }

  if (response.ok) {
    try {
      const data = loanDataUploadResponseSchema.parse(await response.json());

      return { kind: 'success', data };
    } catch {
      return {
        kind: 'network_error',
        message:
          'The server took the file but its reply could not be read. Check the loan data before uploading again.',
      };
    }
  }

  const fallbackBody: LoanDataUploadErrorBody = { error: response.statusText || `HTTP ${response.status}` };

  try {
    const parsedBody = loanDataUploadErrorBodySchema.safeParse(await response.json());

    return { kind: 'http_error', status: response.status, body: parsedBody.success ? parsedBody.data : fallbackBody };
  } catch {
    return { kind: 'http_error', status: response.status, body: fallbackBody };
  }
};
